import React, { useState, ChangeEvent, useEffect } from 'react';
import { ModalWithButtons } from '../../ModalWithButtons/ModalWithButtons.component';
import { Grid, TextField, FormHelperTextProps } from '@material-ui/core';
import { useStyles } from './ContactDataEditor.styles';
import { LeavePrompt } from './../../Prompt/LeavePrompt/LeavePrompt.component';
import { ValidationRow } from '../../ValidationRow/ValidationRow.components';

const DESCRIPTION_MAX_LENGTH = 100;
const CONTACT_INFO_MAX_LENGTH = 100;

interface ContactDataEditorProps {
  title: string;
  onCancel: () => void;
  onSave: (description: string, contactInfo: string) => void;
  onChange: () => void;
  description: string;
  contactInfo: string;
}

export const ContactDataEditor: React.FC<ContactDataEditorProps> = ({
  title,
  onCancel,
  onSave,
  description: descriptionProp,
  contactInfo: contactInfoProp
}) => {
  const classes = useStyles();
  const [isLeavePromptOpen, setIsLeavePromptOpen] = useState<boolean>(false);
  const [description, setDescription] = useState<string>(descriptionProp ?? '');
  const [contactInfo, setContactInfo] = useState<string>(contactInfoProp ?? '');
  const [hasDescriptionFocus, setHasDescriptionFocus] = useState<boolean>(false);
  const [hasContactInfoFocus, setHasContactInfoFocus] = useState<boolean>(false);
  const [isDataChanged, setIsDataChanged] = useState<boolean>(false);

  const handleDescriptionChange = (value: string) => {
    setDescription(value);
  };

  const handleContactInfoChange = (value: string) => {
    setContactInfo(value);
  };

  useEffect(() => {
    setIsDataChanged(descriptionProp !== description || contactInfo !== contactInfoProp);
  }, [descriptionProp, description, contactInfo, contactInfoProp]);

  const isDescriptionTooLong = description.length > DESCRIPTION_MAX_LENGTH;
  const isContactInfoTooLong = contactInfo.length > CONTACT_INFO_MAX_LENGTH;

  const handleCancel = () => {
    isDataChanged ? setIsLeavePromptOpen(true) : onCancel();
  };

  return (
    <>
      <ModalWithButtons
        xCloseButton={handleCancel}
        open={true}
        size="xs"
        title={title}
        autoHeight
        onCancel={handleCancel}
        onConfirm={() => onSave(description, contactInfo)}
        confirmButtonDisabled={
          isDescriptionTooLong ||
          isContactInfoTooLong ||
          !isDataChanged
        }
        handleClose={() => setIsLeavePromptOpen(true)}
      >
        <Grid container>
          <Grid item xs={12} className={classes.item}>
            <TextField
              value={description}
              onChange={(e: ChangeEvent<HTMLInputElement>) => handleDescriptionChange(e.target.value)}
              label="Opis"
              variant="outlined"
              fullWidth
              onFocus={() => setHasDescriptionFocus(true)}
              onBlur={() => setHasDescriptionFocus(false)}
              error={isDescriptionTooLong}
              FormHelperTextProps={
                {
                  component: 'div',
                } as Partial<FormHelperTextProps<'p'>>
              }
              helperText={
                <ValidationRow
                  helperText={
                    isDescriptionTooLong ? 'Podany opis jest za długi' : ''
                  }
                  limitsVisible={hasDescriptionFocus || isDescriptionTooLong}
                  valueLength={description.length}
                  limit={DESCRIPTION_MAX_LENGTH}
                  withError={isDescriptionTooLong}
                />
              }
            />
          </Grid>
          <Grid item xs={12} className={classes.item}>
            <TextField
              value={contactInfo}
              multiline
              onChange={(e: ChangeEvent<HTMLInputElement>) => handleContactInfoChange(e.target.value)}
              label="Dane kontaktowe"
              variant="outlined"
              fullWidth
              inputProps={{
                style: {
                  height: '100px',
                },
              }}
              onFocus={() => setHasContactInfoFocus(true)}
              onBlur={() => setHasContactInfoFocus(false)}
              error={isContactInfoTooLong}
              FormHelperTextProps={
                {
                  component: 'div',
                } as Partial<FormHelperTextProps<'p'>>
              }
              helperText={
                <ValidationRow
                  helperText={isContactInfoTooLong ? 'Podane dane kontaktowe są za długie' : ''}
                  limitsVisible={hasContactInfoFocus || isContactInfoTooLong}
                  valueLength={contactInfo.length}
                  limit={CONTACT_INFO_MAX_LENGTH}
                  withError={isContactInfoTooLong}
                />
              }
            />
          </Grid>
        </Grid>
      </ModalWithButtons>
      <LeavePrompt isOpen={isLeavePromptOpen} onConfirm={onCancel} onCancel={() => setIsLeavePromptOpen(false)} />
    </>
  );
};
